/**
 * Every local write goes through here.
 *
 * The invariant: applying the row to Dexie and appending the outbox op happen
 * in ONE Dexie transaction. If they could diverge you would get either a row
 * that never syncs (written locally, op lost) or a phantom op (op written, row
 * lost) — and both are silent. Neither is recoverable without the reset button.
 *
 * Nothing here touches the network. The flusher in ../sync/engine.ts drains the
 * outbox whenever there happens to be a connection.
 */

import { db, type Metric, type OutboxOp, type SetRow, type Session } from './index'
import { dayOf } from '../lib/day'
import { nextSetIndex, type Side } from '../lib/rounds'

/** UUIDv4 with a fallback — crypto.randomUUID needs a secure context, and the
 *  Android WebView on https://localhost qualifies, but a plain-http dev server
 *  does not. Falling back keeps `npm run dev` working. */
export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function nowIso(): string {
  return new Date().toISOString()
}

type Entity = OutboxOp['entity']

/**
 * Apply a change locally and queue it for the server, atomically.
 *
 * `payload` is what gets sent. It carries the full row rather than a diff:
 * the server upserts by primary key, so a full row is idempotent under replay
 * and under reordering, whereas a diff is neither.
 */
async function stage(
  entity: Entity,
  op: 'upsert' | 'delete',
  table: 'sessions' | 'sets' | 'metrics',
  rowId: string,
  apply: () => Promise<void>,
  payload: Record<string, unknown>,
): Promise<void> {
  await db.transaction('rw', db[table], db.outbox, async () => {
    await apply()
    await db.outbox.put({
      op_id: uuid(),
      entity,
      op,
      row_id: rowId,
      payload,
      created_at: Date.now(),
      attempts: 0,
    })
  })
}

// --- sessions ---------------------------------------------------------------

export async function startSession(input: Partial<Session> = {}): Promise<Session> {
  const ts = nowIso()
  const row: Session = {
    id: uuid(),
    plan_id: input.plan_id ?? null,
    plan_day_id: input.plan_day_id ?? null,
    title: input.title ?? null,
    started_at: input.started_at ?? ts,
    finished_at: null,
    notes: null,
    perceived_fatigue: null,
    bodyweight_kg: null,
    updated_at: ts,
    deleted_at: null,
    seq: 0,
  }
  await stage('session', 'upsert', 'sessions', row.id,
    () => db.sessions.put(row).then(() => undefined), { ...row })
  return row
}

export async function updateSession(
  id: string,
  patch: Partial<Session>,
): Promise<void> {
  const existing = await db.sessions.get(id)
  if (!existing) return
  const row: Session = { ...existing, ...patch, updated_at: nowIso() }
  await stage('session', 'upsert', 'sessions', id,
    () => db.sessions.put(row).then(() => undefined), { ...row })
}

export async function finishSession(id: string): Promise<void> {
  const session = await db.sessions.get(id)

  // A workout logged for another day didn't finish now — it finished whenever
  // its last set was performed. Stamping the wall clock would record a Tuesday
  // session that ran for three days, and every duration and adherence check
  // downstream would believe it.
  //
  // The comparison is on LOCAL days and is `!==`, not `<`. Slicing the UTC day
  // off the two timestamps made an evening backdate look like today (8pm here is
  // already tomorrow in UTC), so the guard skipped the case it was written for.
  // And `<` ignored dated-ahead sessions, which the recorder explicitly offers:
  // those got finished_at = now, i.e. *before* they started, which is how rows
  // ended up in the database claiming to have finished three days early.
  let finished = nowIso()
  if (session && dayOf(session.started_at) !== dayOf(finished)) {
    const sets = await db.sets.where('session_id').equals(id).toArray()
    const last = sets
      .filter((s) => !s.deleted_at)
      .map((s) => s.performed_at)
      .sort()
      .pop()
    finished = last ?? session.started_at
  }

  await updateSession(id, { finished_at: finished })
}

export async function deleteSession(id: string): Promise<void> {
  const ts = nowIso()
  // Cascade locally so the UI updates immediately, and queue a tombstone per
  // set. The server soft-deletes each row it's told about; it does not infer
  // the cascade, because a set could in principle have been moved.
  const sets = await db.sets.where('session_id').equals(id).toArray()
  await updateSessionTombstone(id, ts)
  for (const s of sets) {
    if (!s.deleted_at) await deleteSet(s.id)
  }
}

async function updateSessionTombstone(id: string, ts: string): Promise<void> {
  const existing = await db.sessions.get(id)
  if (!existing) return
  const row: Session = { ...existing, deleted_at: ts, updated_at: ts }
  await stage('session', 'delete', 'sessions', id,
    () => db.sessions.put(row).then(() => undefined),
    { id, updated_at: ts })
}

// --- sets -------------------------------------------------------------------

export type NewSet = {
  session_id: string
  exercise_id: number
  weight_kg?: number | null
  reps?: number | null
  duration_seconds?: number | null
  distance_m?: number | null
  rpe?: number | null
  is_warmup?: boolean
  is_failure?: boolean
  notes?: string | null
  performed_at?: string
}

export async function addSet(input: NewSet): Promise<SetRow> {
  const ts = nowIso()

  // set_index is per exercise within the session ("set 3 of bench"), which is
  // what the user sees; order_index is the position in the whole session, which
  // is what preserves the order they actually did things in.
  const siblings = await db.sets.where('session_id').equals(input.session_id).toArray()
  const live = siblings.filter((s) => !s.deleted_at)
  const sameExercise = live.filter((s) => s.exercise_id === input.exercise_id)

  const row: SetRow = {
    id: uuid(),
    session_id: input.session_id,
    exercise_id: input.exercise_id,
    performed_at: input.performed_at ?? ts,
    order_index: live.length,
    set_index: sameExercise.length + 1,
    weight_kg: input.weight_kg ?? null,
    reps: input.reps ?? null,
    duration_seconds: input.duration_seconds ?? null,
    distance_m: input.distance_m ?? null,
    rpe: input.rpe ?? null,
    is_warmup: input.is_warmup ?? false,
    is_failure: input.is_failure ?? false,
    e1rm: null, // server computes this; leaving it null is honest
    notes: input.notes ?? null,
    updated_at: ts,
    deleted_at: null,
    seq: 0,
  }

  await stage('set', 'upsert', 'sets', row.id,
    () => db.sets.put(row).then(() => undefined), { ...row })
  return row
}

/** What one side of a round measured. */
export type SideMeasures = Pick<NewSet, 'weight_kg' | 'reps' | 'duration_seconds' | 'distance_m'>

/**
 * One set of a one-sided exercise: a left row and a right row sharing a
 * set_index. See lib/rounds.
 *
 * Both rows in ONE transaction. A crash between two separate writes would leave
 * half a round, which the plan then refuses to count as a set — a set somebody
 * did, silently not counting.
 *
 * The note goes on the left row only. It describes the set, and copying it onto
 * both would print it twice everywhere notes are listed.
 */
export async function addRound(
  input: Omit<NewSet, keyof SideMeasures>,
  left: SideMeasures,
  right: SideMeasures,
): Promise<SetRow[]> {
  const ts = nowIso()
  const rows: SetRow[] = []

  await db.transaction('rw', db.sets, db.outbox, async () => {
    const siblings = await db.sets.where('session_id').equals(input.session_id).toArray()
    const live = siblings.filter((s) => !s.deleted_at)
    // Past the highest, not a count: a count of rows is two per round.
    const setIndex = nextSetIndex(live.filter((s) => s.exercise_id === input.exercise_id))

    const make = (side: Side, m: SideMeasures, offset: number): SetRow => ({
      id: uuid(),
      session_id: input.session_id,
      exercise_id: input.exercise_id,
      performed_at: input.performed_at ?? ts,
      order_index: live.length + offset,
      set_index: setIndex,
      side,
      weight_kg: m.weight_kg ?? null,
      reps: m.reps ?? null,
      duration_seconds: m.duration_seconds ?? null,
      distance_m: m.distance_m ?? null,
      rpe: input.rpe ?? null,
      is_warmup: input.is_warmup ?? false,
      is_failure: input.is_failure ?? false,
      e1rm: null,
      notes: side === 'L' ? input.notes ?? null : null,
      updated_at: ts,
      deleted_at: null,
      seq: 0,
    })

    rows.push(make('L', left, 0), make('R', right, 1))
    // stage() opens its own transaction over the same two tables, which Dexie
    // runs as a part of this one.
    for (const row of rows) {
      await stage('set', 'upsert', 'sets', row.id,
        () => db.sets.put(row).then(() => undefined), { ...row })
    }
  })
  return rows
}

export async function updateSet(id: string, patch: Partial<SetRow>): Promise<void> {
  const existing = await db.sets.get(id)
  if (!existing) return
  const row: SetRow = { ...existing, ...patch, updated_at: nowIso() }
  // Any change to weight or reps invalidates the server's e1rm. Clear it rather
  // than recomputing locally — the server is the single author of derived data,
  // and a client that computes it slightly differently would produce charts
  // that disagree with the PR table.
  if ('weight_kg' in patch || 'reps' in patch || 'is_warmup' in patch) {
    row.e1rm = null
  }
  await stage('set', 'upsert', 'sets', id,
    () => db.sets.put(row).then(() => undefined), { ...row })
}

export async function deleteSet(id: string): Promise<void> {
  const ts = nowIso()
  const existing = await db.sets.get(id)
  if (!existing) return
  const row: SetRow = { ...existing, deleted_at: ts, updated_at: ts }
  await stage('set', 'delete', 'sets', id,
    () => db.sets.put(row).then(() => undefined),
    { id, updated_at: ts })
}

// --- body metrics -----------------------------------------------------------
//
// Weigh-ins ride the same outbox as sets, so stepping on the scale works with
// no signal exactly like logging a set does. The server already accepted this
// entity from the first migration — nothing in the UI had ever written one.

export async function addMetric(input: Partial<Metric>): Promise<Metric> {
  const ts = nowIso()
  const row: Metric = {
    id: uuid(),
    measured_at: input.measured_at ?? ts,
    weight_kg: input.weight_kg ?? null,
    bodyfat_pct: input.bodyfat_pct ?? null,
    measurements_json: input.measurements_json ?? null,
    notes: input.notes ?? null,
    updated_at: ts,
    deleted_at: null,
    seq: 0,
  }
  await stage('metric', 'upsert', 'metrics', row.id,
    () => db.metrics.put(row).then(() => undefined), { ...row })
  return row
}

export async function updateMetric(id: string, patch: Partial<Metric>): Promise<void> {
  const existing = await db.metrics.get(id)
  if (!existing) return
  const row: Metric = { ...existing, ...patch, updated_at: nowIso() }
  await stage('metric', 'upsert', 'metrics', id,
    () => db.metrics.put(row).then(() => undefined), { ...row })
}

export async function deleteMetric(id: string): Promise<void> {
  const ts = nowIso()
  const existing = await db.metrics.get(id)
  if (!existing) return
  // Soft delete, same as everything else: a hard delete is invisible to the
  // pull cursor, so the row would come back from any other device that still
  // had it.
  const row: Metric = { ...existing, deleted_at: ts, updated_at: ts }
  await stage('metric', 'delete', 'metrics', id,
    () => db.metrics.put(row).then(() => undefined),
    { id, updated_at: ts })
}
